import { Button, Dropdown, FormLayout, TextInput } from 'components/common'
import { useForm } from 'react-hook-form'
import { configRootFields, configurationsFields, expressionType } from './utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { configurationSchema } from 'utils/schema/configurationsSchema'
import { useState } from 'react'
import ManualCrons from './manualCrons'
import { ERROR_CODES } from 'utils/constants'
import toast from 'react-hot-toast'
import { createConfigurations, updateConfigurations } from 'utils/api/configApi'
import { AxiosError } from 'axios'
import { Configuration, ConfigurationWithId } from 'types/configuration'
import StoresDropdown from './storesDropdown'
import AggregatorDropdown from 'components/common/aggregatorDropdown'
import ErpSection from './erpSection'
import { getNestedErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import RmaSection from './rmaSection'

const {
  SQL_USER_ERP_DB,
  SQL_HOST,
  SQL_PORT,
  SQL_USERNAME,
  SQL_PASSWORD,
  SQL_DATABASE_NAME,

  SMTP_HOST,
  SMTP_USERNAME,
  SMTP_PASSWORD,
  SMTP_FROM_EMAIL,
  SMTP_FROM_NAME,

  EXPRESSION_MASTER,
  EXPRESSION_INVENTORY,
  EXPRESSION_PRICING,
  EXPRESSION_ORDER_SYNC,
  EXPRESSION_RECONCILIATION,

  INVOICE_ADDRESS,
  INVOICE_PHONE,
  INVOICE_REG_NO,

  MA_AGGREGATOR,
  MA_STORE,
  MA_LIMIT_INVENTORY,
  MA_LIMIT_PRICING,

  ADMIN_EMAIL,
  MAX_UPLOAD_SIZE,
  LOG_DELETION_PERIOD,
  MAX_CONCURRENT_AGGREGATORS,
  FRESH_BARCODE_PREFIX,
} = configurationsFields

type Props = {
  configuration?: ConfigurationWithId
  isConfigured: boolean
  completeConfiguration: () => void
}

const ConfigurationForm = ({
  configuration,
  isConfigured,
  completeConfiguration,
}: Props) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(configurationSchema),
    defaultValues: {
      ...configuration,
      [FRESH_BARCODE_PREFIX.key]:
        configuration?.[FRESH_BARCODE_PREFIX.key]?.join(',').trim() ?? '',
    },
  })

  const [loading, setLoading] = useState(false)

  const handleChangeConfigurations = async (data: Configuration) => {
    const configToast = toast.loading(
      `${isConfigured ? 'Updating' : 'Creating'} Configurations...`,
    )

    const config = {
      ...data,
      freshBarcodePrefix: data?.freshBarcodePrefix
        ? (data?.freshBarcodePrefix as unknown as string).split(',')
        : [],
    }

    setLoading(true)

    try {
      // if configurations are already configured
      // update the configurations
      if (isConfigured) {
        await updateConfigurations(configuration!._id, config)
        toast.success('Configuration updated successfully.', {
          id: configToast,
        })
      } else {
        // create configurations
        await createConfigurations(config)
        completeConfiguration()
        toast.success('Configuration created successfully.', {
          id: configToast,
        })
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const MESSAGE = error?.response?.data?.errors?.msg

        if (MESSAGE?.[0]?.msg === ERROR_CODES.INVALID_CRON_EXPRESSION) {
          const cronId = MESSAGE?.[0]?.param?.split('.')[1]

          if (expressionType[cronId]) {
            return toast.error(`Invalid ${expressionType[cronId]}`, {
              id: configToast,
            })
          }
        }

        toast.error(
          `Failed to ${isConfigured ? 'Update' : 'Create'} Configurations.`,
          { id: configToast },
        )
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-6xl">
      {!isConfigured && (
        <h1 className="title text-3xl">Create Configuration</h1>
      )}

      <form onSubmit={handleSubmit(handleChangeConfigurations)}>
        {/* ERP Master */}
        <ErpSection
          title="Master ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_MASTER}
        />

        {/* ERP Inventory */}
        <ErpSection
          title="Inventory ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_INVENTORY}
        />

        {/* ERP Fresh System  */}
        <ErpSection
          title="Fresh System Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.FRESH}
        />

        {/* ERP Pricing */}
        <ErpSection
          title="Pricing ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_PRICING}
        />

        {/* ERP Block Sale Status */}
        <ErpSection
          title="Block Sale Status ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.BLOCK_SALE_POS}
        />

        {/* ERP Order Create */}
        <ErpSection
          title="Order Create ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_ORDER_CREATE}
        />

        {/* ERP Reconciliation */}
        <ErpSection
          title="Reconciliation ERP Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.ERP_RECONCILIATION}
        />

        {/* Last Mile */}
        <ErpSection
          title="Last Mile Configuration"
          errors={errors}
          register={register}
          parentKey={configRootFields.LAST_MILE}
          isBearerToken
        />

        {/* SQL */}
        <h3 className="title mt-3">SQL Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            <Dropdown
              setValue={setValue}
              defaultValue={configuration?.sql?.useErpDb ?? false}
              options={[
                { label: 'Database', value: true },
                { label: 'API', value: false },
              ]}
              name={SQL_USER_ERP_DB.parentKey + '.' + SQL_USER_ERP_DB.key}
              label={SQL_USER_ERP_DB.label}
              error={errors?.[SQL_USER_ERP_DB.key]?.message as string}
              hintText="This selection will affect both inventory and pricing crons."
            />
            {/* SQL Host */}
            <TextInput
              required
              register={register}
              name={SQL_HOST.parentKey + '.' + SQL_HOST.key}
              label={SQL_HOST.label}
              error={getNestedErrorMessage(errors, SQL_HOST)}
            />

            {/* SQL Username */}
            <TextInput
              required
              register={register}
              name={SQL_USERNAME.parentKey + '.' + SQL_USERNAME.key}
              label={SQL_USERNAME.label}
              error={getNestedErrorMessage(errors, SQL_USERNAME)}
            />

            {/* SQL Password */}
            <TextInput
              required
              type="password"
              register={register}
              name={SQL_PASSWORD.parentKey + '.' + SQL_PASSWORD.key}
              label={SQL_PASSWORD.label}
              error={getNestedErrorMessage(errors, SQL_PASSWORD)}
            />

            {/* SQL Port */}
            <TextInput
              required
              register={register}
              name={SQL_PORT.parentKey + '.' + SQL_PORT.key}
              label={SQL_PORT.label}
              error={getNestedErrorMessage(errors, SQL_PORT)}
            />

            {/* SQL Database Name */}
            <TextInput
              required
              register={register}
              name={SQL_DATABASE_NAME.parentKey + '.' + SQL_DATABASE_NAME.key}
              label={SQL_DATABASE_NAME.label}
              error={getNestedErrorMessage(errors, SQL_DATABASE_NAME)}
            />
          </div>
        </FormLayout>

        {/* SMTP */}
        <h3 className="title mt-3">SMTP Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            {/* SMTP Host */}
            <TextInput
              required
              register={register}
              name={SMTP_HOST.parentKey + '.' + SMTP_HOST.key}
              label={SMTP_HOST.label}
              error={getNestedErrorMessage(errors, SMTP_HOST)}
            />

            {/* SMTP Username */}
            <TextInput
              required
              register={register}
              name={SMTP_USERNAME.parentKey + '.' + SMTP_USERNAME.key}
              label={SMTP_USERNAME.label}
              error={getNestedErrorMessage(errors, SMTP_USERNAME)}
            />

            {/* SMTP Password */}
            <TextInput
              required
              type="password"
              register={register}
              name={SMTP_PASSWORD.parentKey + '.' + SMTP_PASSWORD.key}
              label={SMTP_PASSWORD.label}
              error={getNestedErrorMessage(errors, SMTP_PASSWORD)}
            />

            {/* SMTP From Name */}
            <TextInput
              required
              register={register}
              name={SMTP_FROM_NAME.parentKey + '.' + SMTP_FROM_NAME.key}
              label={SMTP_FROM_NAME.label}
              error={getNestedErrorMessage(errors, SMTP_FROM_NAME)}
              hintText="Enter the name you want to appear as the sender in emails"
            />

            {/* SMTP From Email */}
            <TextInput
              required
              register={register}
              name={SMTP_FROM_EMAIL.parentKey + '.' + SMTP_FROM_EMAIL.key}
              label={SMTP_FROM_EMAIL.label}
              error={getNestedErrorMessage(errors, SMTP_FROM_EMAIL)}
              hintText="Enter the email address you want to use as the sender for emails"
            />
          </div>
        </FormLayout>

        {/* Invoice Config */}
        <h3 className="title mt-3">Invoice Configurations</h3>
        <FormLayout>
          <div className="flex flex-col gap-3">
            {/* Vat Reg */}
            <TextInput
              required
              register={register}
              name={INVOICE_REG_NO.parentKey + '.' + INVOICE_REG_NO.key}
              label={INVOICE_REG_NO.label}
              error={getNestedErrorMessage(errors, INVOICE_REG_NO)}
            />

            <TextInput
              required
              register={register}
              name={INVOICE_PHONE.parentKey + '.' + INVOICE_PHONE.key}
              label={INVOICE_PHONE.label}
              error={getNestedErrorMessage(errors, INVOICE_PHONE)}
            />

            <TextInput
              required
              register={register}
              name={INVOICE_ADDRESS.parentKey + '.' + INVOICE_ADDRESS.key}
              label={INVOICE_ADDRESS.label}
              error={getNestedErrorMessage(errors, INVOICE_ADDRESS)}
            />
          </div>
        </FormLayout>

        {/* cron */}
        <h3 className="title mt-3">Cron Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 mid-md:grid-cols-3">
            {/* Cron Master data */}
            <TextInput
              required
              register={register}
              name={EXPRESSION_MASTER.parentKey + '.' + EXPRESSION_MASTER.key}
              label={EXPRESSION_MASTER.label}
              error={getNestedErrorMessage(errors, EXPRESSION_MASTER)}
              placeholder="e.g., 0 2 * *"
            />

            {/* Cron Inventory */}
            <TextInput
              required
              register={register}
              name={
                EXPRESSION_INVENTORY.parentKey + '.' + EXPRESSION_INVENTORY.key
              }
              label={EXPRESSION_INVENTORY.label}
              error={getNestedErrorMessage(errors, EXPRESSION_INVENTORY)}
              placeholder="e.g., 0 2 * *"
            />

            {/* Cron Pricing */}
            <TextInput
              required
              register={register}
              name={EXPRESSION_PRICING.parentKey + '.' + EXPRESSION_PRICING.key}
              label={EXPRESSION_PRICING.label}
              error={getNestedErrorMessage(errors, EXPRESSION_PRICING)}
              placeholder="e.g., 0 2 * *"
            />

            {/* Cron Order Sync */}
            <TextInput
              required
              register={register}
              name={
                EXPRESSION_ORDER_SYNC.parentKey +
                '.' +
                EXPRESSION_ORDER_SYNC.key
              }
              label={EXPRESSION_ORDER_SYNC.label}
              error={getNestedErrorMessage(errors, EXPRESSION_ORDER_SYNC)}
              placeholder="e.g., 0 2 * *"
            />

            {/* Cron Reconciliation Sync */}
            <TextInput
              required
              register={register}
              name={
                EXPRESSION_RECONCILIATION.parentKey +
                '.' +
                EXPRESSION_RECONCILIATION.key
              }
              label={EXPRESSION_RECONCILIATION.label}
              error={getNestedErrorMessage(errors, EXPRESSION_RECONCILIATION)}
              placeholder="e.g., 0 2 * *"
            />

            <div className="mid-md:col-span-3">
              <hr className="my-6" />
              <ManualCrons />
            </div>
          </div>
        </FormLayout>

        {/* MAGENTO */}
        <h3 className="title mt-3">Magento API Configurations</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            {/* MAGENTO STORE */}
            <StoresDropdown
              withNoneOption
              required
              defaultValue={configuration?.magentoApi?.store}
              setValue={setValue}
              name={MA_STORE.parentKey + '.' + MA_STORE.key}
              label={MA_STORE.label}
              error={getNestedErrorMessage(errors, MA_STORE)}
            />

            {/* MAGENTO AGGREGATOR */}
            <AggregatorDropdown
              required
              withNoneOption
              setValue={setValue}
              defaultValue={configuration?.magentoApi?.aggregator}
              name={MA_AGGREGATOR.parentKey + '.' + MA_AGGREGATOR.key}
              label={MA_AGGREGATOR.label}
              error={getNestedErrorMessage(errors, MA_AGGREGATOR)}
            />

            {/* MAGENTO API LIMIT INVENTORY */}
            <TextInput
              required
              register={register}
              name={MA_LIMIT_INVENTORY.parentKey + '.' + MA_LIMIT_INVENTORY.key}
              label={MA_LIMIT_INVENTORY.label}
              error={getNestedErrorMessage(errors, MA_LIMIT_INVENTORY)}
            />

            {/* MAGENTO API LIMIT PRICING */}
            <TextInput
              required
              register={register}
              name={MA_LIMIT_PRICING.parentKey + '.' + MA_LIMIT_PRICING.key}
              label={MA_LIMIT_PRICING.label}
              error={getNestedErrorMessage(errors, MA_LIMIT_PRICING)}
            />
          </div>
        </FormLayout>

        {/* RMA Configuration */}
        <RmaSection
          setValue={setValue}
          condition={configuration?.rmaConditions}
          reason={configuration?.rmaReasons}
          errors={errors}
        />

        {/* Other */}
        <h3 className="title mt-3">Other</h3>
        <FormLayout>
          <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
            {/* Max Upload Size */}
            <TextInput
              required
              register={register}
              name={MAX_UPLOAD_SIZE.key}
              label={MAX_UPLOAD_SIZE.label}
              error={
                (errors as Record<string, any>)?.[MAX_UPLOAD_SIZE.key]?.message
              }
              hintText="Specify the maximum file size allowed for uploads. Enter the size in megabytes (MB)."
            />

            {/* Log Deletion Period */}
            <TextInput
              required
              register={register}
              name={LOG_DELETION_PERIOD.key}
              label={LOG_DELETION_PERIOD.label}
              error={
                (errors as Record<string, any>)?.[LOG_DELETION_PERIOD.key]
                  ?.message
              }
              hintText="Enter the number of days to retain logs. Logs older than this duration will be automatically deleted."
            />

            {/* Admin Email */}
            <TextInput
              required
              register={register}
              name={ADMIN_EMAIL.key}
              label={ADMIN_EMAIL.label}
              error={
                (errors as Record<string, any>)?.[ADMIN_EMAIL.key]?.message
              }
              hintText="Provide an email to receive error notifications. Separate multiple emails with commas (',')"
            />

            {/* MAX_CONCURRENT_AGGREGATORS */}
            <TextInput
              required
              register={register}
              name={MAX_CONCURRENT_AGGREGATORS.key}
              label={MAX_CONCURRENT_AGGREGATORS.label}
              error={
                (errors as Record<string, any>)?.[
                  MAX_CONCURRENT_AGGREGATORS.key
                ]?.message
              }
              hintText="Specify maximum number of concurrent aggregator processes"
            />

            {/* Fresh Item Prefix */}
            <TextInput
              register={register}
              name={FRESH_BARCODE_PREFIX.key}
              label={FRESH_BARCODE_PREFIX.label}
              error={
                (errors as Record<string, any>)?.[FRESH_BARCODE_PREFIX.key]
                  ?.message
              }
              hintText="Enter one or more prefixes to identify fresh items. Separate multiple prefixes with commas (',')"
            />
          </div>
        </FormLayout>

        <Button loading={loading} buttonText="Save" className="mt-4 w-32" />
      </form>
    </div>
  )
}

export default ConfigurationForm
